import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { GetSupportedPispInstitutions } from '@app/finxone-web-frontend/app/lib/actions/savings-account.action';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { SavingsAccountService } from '@app/finxone-web-frontend/app/lib/services/savings-account-service/savings-account.service';
import { PispSupportedInstitution } from '@app/finxone-web-frontend/app/lib/services/savings-account-service/savings-account.type';
import { ApiActions, ApiLoadingState } from '@app/finxone-web-frontend/app/lib/state/api-loading.state';
import { SavingsAccountState } from '@app/finxone-web-frontend/app/lib/state/savings-account.state';
import { ProfileState } from '@app/finxone-web-frontend/app/lib/state/user-profile.state';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { FormID } from '@finxone-platform/form-action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  AlertTypes,
  baseCssConfigStyle,
  BaseWidgetProperties,
  commonContentStyleList,
  commonTextContentStyleList,
  ConfigJsonType,
  ProviderClass,
  WidgetNames,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import { firstValueFrom, Observable, Subject, takeUntil, tap } from 'rxjs';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import { appendBillingRequestIdToUrl } from '@app/finxone-web-frontend/app/lib/utils/cta-button-actions/savings-account-actions/pisp-deposit.utils';
import { BaseComponent } from '../base/base.component';
import { ZonePageUrlProvider } from '../../../zones/zone-url-provider';
import { NavigationService } from '@app/finxone-web-frontend/app/lib/services/navigation-service/navigation.service';
import { RoleState, RoleStateModel } from '@app/finxone-web-frontend/app/lib/state/role.state';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-pisp-banks-listing-widget',
  templateUrl: './pisp-banks-listing-widget.component.html',
  styleUrls: ['./pisp-banks-listing-widget.component.scss'],
})
export class PispBanksListingWidgetComponent extends BaseComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @Select(SavingsAccountState.getPispSupportedInstitutions)
  private supportedInstitutions$!: Observable<PispSupportedInstitution[]>;
  @Select(ApiLoadingState.getApiState(ApiActions.supportedPispInstitutionsLoading))
  private getSupportedPispInstitutionsLoadingState$: Observable<{ isLoading: boolean }>;
  public widgetConfig: BaseWidgetProperties;
  public textContentStyle: baseCssConfigStyle;
  public searchIconStyle: baseCssConfigStyle;
  public searchInputStyle: baseCssConfigStyle;
  public searchInputInactiveStyle: baseCssConfigStyle;
  public searchInputBaseStyle: baseCssConfigStyle;
  public supportedInstitutions: PispSupportedInstitution[];
  public widgetName = WidgetNames.PISP_BANK_LIST_WIDGET;
  // Set from the `feature` URL query param in ngOnInit - see reviewPispDeposit for where it's
  // attached. Reused in handleSelection so the billing request it mints matches the same intent
  // the institution list itself was just filtered by.
  private isRecurringDeposit = false;
  public config: ConfigJsonType;
  public isApiInProgress = false;

  @Select(RoleState.getRole)
  roles!: Observable<RoleStateModel>;
  roleInfo: RoleStateModel;

  public dataIn: ProviderClass<any> = inject(ProviderClass);
  public store: Store = inject(Store);
  public configService: ConfigService = inject(ConfigService);
  public savingsAccountService: SavingsAccountService = inject(SavingsAccountService);
  private navigationService: NavigationService = inject(NavigationService);
  private zonePageUrlProvider: ZonePageUrlProvider = inject(ZonePageUrlProvider);
  private workflowFormsService: WorkflowFormsService = inject(WorkflowFormsService);
  private alertHandlerService: AlertHandlerService = inject(AlertHandlerService);

  constructor() {
    super();
    this.roles.pipe(takeUntil(this.destroy$)).subscribe((roles) => {
      this.roleInfo = roles;
    });
  }

  ngOnInit(): void {
    if (this.dataIn?.attri) {
      this.widgetConfig = this.dataIn.attri.widgetProperties;
    }
    this.getWidgetCssConfig();

    const country = this.store.selectSnapshot(ProfileState.getProfile)?.country;
    // Present on the "no institution selected" redirect - see reviewPispDeposit - so this widget
    // can show the institution catalog matching the intent (recurring deposits need VRP-capable
    // institutions, which the plain PIS-scoped list doesn't reliably contain). Absent by default:
    // real users landing here any other way still see the standard unfiltered list.
    const feature = new URLSearchParams(window.location.search).get('feature') ?? undefined;
    this.isRecurringDeposit = feature === 'vrp';
    this.store.dispatch(new GetSupportedPispInstitutions(country, feature));
    this.supportedInstitutions$
      .pipe(
        takeUntil(this.destroy$),
        tap((response) => {
          this.supportedInstitutions = response;
        }),
      )
      .subscribe();

    this.setupLoadingStateSubscription();
  }

  private setupLoadingStateSubscription(): void {
    this.getSupportedPispInstitutionsLoadingState$.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      this.isApiInProgress = result.isLoading;
    });
  }

  public getWidgetCssConfig() {
    this.contentStyle = this.configService.getSpecificBaseCssStyle(
      this.dataIn?.attri,
      commonContentStyleList,
    );

    this.textContentStyle = this.configService.getSpecificBaseCssStyle(
      this.dataIn?.attri,
      commonTextContentStyleList,
    );

    const { iconStyle, baseStyle, activeStyle, inactiveStyle } = this.configService.getInputFieldCssStyles(
      this.widgetConfig,
    );

    this.searchIconStyle = iconStyle;
    this.searchInputStyle = baseStyle;
    this.searchInputBaseStyle = activeStyle;
    this.searchInputInactiveStyle = inactiveStyle;
  }

  public async handleSelection(institution: PispSupportedInstitution) {
    const dataIn = this.dataIn;
    let pageUrl: string | undefined;
    await firstValueFrom(this.savingsAccountService.setPispInstituionOnNomiantedAccount(institution.id));

    const pageId: string | undefined = dataIn.attri['pageId'] ?? undefined;

    if (!pageId) {
      return;
    }

    const { pageUrl: url } = this.navigationService.buildPageNavigationUrl({
      pageId: pageId || '', // using this it will generate url based on pageId and ignore pageUrl, if pageId is present in widget config
      zone: dataIn.attri?.zone || '',
      pageUrl: pageUrl || '',
      role: dataIn.attri?.role || '',
      navigationOption: dataIn.attri['navigationOption'],
      tabRef: dataIn.attri?.['tabRef'] ?? '',
      pageTransition: dataIn.attri?.['pageTransition'] ?? 'forward',
      browserBehaviour: dataIn.attri?.['browserBehaviour'],
      externalUrl: dataIn.attri?.['externalUrl'],
    });

    pageUrl = this.checkRouteForParamsAndUpdatePageUrl(url) || '';

    // Mirrors reviewPispDeposit: this navigation also lands on the review page, whose GoCardless
    // compliance widget needs a billing_request_id URL param in the custom payment page flow.
    let targetUrl = url;
    if (this.configService?.config?.goCardlessEnabledCustomPaymentPages) {
      const depositForm = this.workflowFormsService.getAllFormValuesFlattened()?.[FormID.DEPOSIT_FORM];
      const amount = Number(depositForm?.['amount']);
      const accountId = this.store.selectSnapshot(SavingsAccountState.getCurrentAccount)?.accountId;
      if (amount && accountId) {
        try {
          const { billingRequestId } = await firstValueFrom(
            this.savingsAccountService.createPispBillingRequest(accountId, {
              amount,
              isRecurring: this.isRecurringDeposit,
            }),
          );
          targetUrl = appendBillingRequestIdToUrl(url, billingRequestId);
        } catch (error) {
          console.error('Error creating pisp billing request', error);
          this.alertHandlerService.showAlertFn(
            AlertTypes.ERROR,
            'Unable to prepare your deposit. Please try again.',
          );
          return;
        }
      }
    }

    this.navigationService.navigateByPageUrl({
      url: targetUrl,
      targetRole: dataIn.attri?.role || '',
      currentRole: this.roleInfo.role,
      zone: dataIn.attri?.zone || '',
      externalLink: dataIn.attri?.['externalUrl'],
      browserBehaviour: dataIn.attri?.['browserBehaviour'],
    });
  }

  /**
   * Checks if current url has id in route params and return updated page url
   * @param pageUrl - page url from widget config
   */
  checkRouteForParamsAndUpdatePageUrl(pageUrl: string): string | undefined {
    let url = pageUrl;
    if (this.zonePageUrlProvider?.['id']) {
      url = `${pageUrl}/${this.zonePageUrlProvider['id']}`;
    }
    return url;
  }
}
